import { useEffect, useRef, useState } from 'react'
import { useArcTranslation } from 'hooks'
import { useCaroselStyles } from 'components/Carousel'
import {
  Space,
  Flex,
  Box,
  Text,
  Carousel,
  carouselDashboard,
  SimpleGrid,
  Skeleton,
  EmptyStateBox,
} from 'components'
import { IconClipboardText, IconFolderOff } from '@tabler/icons-react'
import { ArcProjectCard } from 'pages/TeacherDashboard/components/ArcProjectsSection/ArcProjectCard'
import { getMyAssignedProjects } from '../../service/studentDashboardService'
import type { ArcProject } from 'pages/ArcProjectsList/types'

const CAROUSEL_THRESHOLD = 3
const CARD_HEIGHT = 380

export function StudentProjectsSection() {
  const { t } = useArcTranslation()
  const { classes } = useCaroselStyles()
  const tr = (k: string): string => t(`student_dashboard.projects.${k}`)

  const [projects, setProjects] = useState<ArcProject[]>([])
  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    const load = async (): Promise<void> => {
      try {
        const result = await getMyAssignedProjects()
        if (mountedRef.current) setProjects(result)
      } catch (error) {
        console.error('Failed to load student projects:', error)
        if (mountedRef.current) setHasError(true)
      } finally {
        if (mountedRef.current) setLoading(false)
      }
    }
    load()
    return () => {
      mountedRef.current = false
    }
  }, [])

  const useCarousel = projects.length > CAROUSEL_THRESHOLD

  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <SimpleGrid cols={3} spacing='md' breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'sm', cols: 1 }]}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={CARD_HEIGHT} radius='md' animate />
          ))}
        </SimpleGrid>
      )
    }

    if (hasError) {
      return <EmptyStateBox withBorder text={tr('error')} icon={<IconFolderOff />} />
    }

    if (projects.length === 0) {
      return <EmptyStateBox withBorder text={tr('empty')} icon={<IconFolderOff />} />
    }

    if (useCarousel) {
      return (
        <Carousel
          {...carouselDashboard}
          draggable={projects.length > 1}
          classNames={{ root: classes.carousel, controls: classes.carouselControls }}
        >
          {projects.map(project => (
            <Carousel.Slide key={project.id}>
              {/* minHeight (not a fixed height) lets the card grow when text
                  wraps at narrow widths; height 100% keeps slides equalized */}
              <Box sx={{ minHeight: CARD_HEIGHT, height: '100%' }}>
                <ArcProjectCard project={project} />
              </Box>
            </Carousel.Slide>
          ))}
        </Carousel>
      )
    }

    return (
      <SimpleGrid cols={3} spacing='md' breakpoints={[{ maxWidth: 'md', cols: 2 }, { maxWidth: 'sm', cols: 1 }]}>
        {projects.map(project => (
          <Box key={project.id} sx={{ minHeight: CARD_HEIGHT }}>
            <ArcProjectCard project={project} />
          </Box>
        ))}
      </SimpleGrid>
    )
  }

  return (
    <>
      <Flex mt={80}>
        <IconClipboardText />
        <Space w='xs' />
        <Text size='lg' weight={700}>
          {tr('title')}
        </Text>
      </Flex>
      <Space h='xs' />
      {renderContent()}
    </>
  )
}
